import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
    X, ChevronRight,
    Home as HomeIcon, Building2, Building, Gavel, TrendingUp,
    Wrench, MapPin, Star, Megaphone, Newspaper, Phone, Info, Video,
} from "lucide-react";
import InstallPWAButton from "./InstallPWAButton";

/* ---------------------------------------------------------------
 * Global Property Mart — Native mobile MENU drawer.
 *
 * Design brief (from user + reference image):
 *   • White background, premium Navy + Gold theme.
 *   • GPM logo header + X close button.
 *   • Each item = colored icon + title + short description.
 *   • Slide-in from RIGHT, dim/blur backdrop.
 *   • Platform-wide navigation ONLY — no profile / dashboard items,
 *     no auth CTAs (those belong in the /profile section).
 * --------------------------------------------------------------- */

const MENU_ITEMS = [
    { key: "home",        icon: HomeIcon,   label: "Home",
      desc: "Go back to app home",
      to: "/",
      tint: { bg: "#DBEAFE", fg: "#2563EB" } },
    { key: "properties",  icon: Building2,  label: "All Properties",
      desc: "Browse for Sale, Rent & Lease",
      to: "/properties",
      tint: { bg: "#FFEDD5", fg: "#EA580C" } },
    { key: "new-projects", icon: Building,  label: "New Projects",
      desc: "Latest launches from top builders",
      to: "/new-projects",
      tint: { bg: "#D1FAE5", fg: "#059669" } },
    { key: "auctions",    icon: Gavel,      label: "Bank Auction",
      desc: "Bank auction properties & deals",
      to: "/auctions",
      tint: { bg: "#EDE9FE", fg: "#7C3AED" } },
    { key: "reels",       icon: Video,      label: "GPM Reels",
      desc: "Short property videos & tours",
      to: "/reels",
      tint: { bg: "#FCE7F3", fg: "#DB2777" } },
    { key: "investment",  icon: TrendingUp, label: "Investment Opportunities",
      desc: "High-return property investments",
      to: "/properties?investment=true",
      tint: { bg: "#FEF3C7", fg: "#D97706" } },
    { key: "services",    icon: Wrench,     label: "All Services",
      desc: "Real-estate services in one place",
      to: "/services",
      tint: { bg: "#DCFCE7", fg: "#16A34A" } },
    { key: "location",    icon: MapPin,     label: "Explore by Location",
      desc: "Search by city, locality & area",
      to: "/property-in-nagpur",
      tint: { bg: "#FEE2E2", fg: "#DC2626" } },
    { key: "featured",    icon: Star,       label: "Featured / Premium Listings",
      desc: "View premium verified properties",
      to: "/properties?featured=true",
      tint: { bg: "#FBF6E0", fg: "#B45309" } },
    { key: "offers",      icon: Megaphone,  label: "Offers & Promotions",
      desc: "Latest offers, discounts & deals",
      to: "/pricing",
      tint: { bg: "#FCE7F3", fg: "#DB2777" } },
    { key: "insights",    icon: Newspaper,  label: "Property Insights",
      desc: "News, market trends, tips & guides",
      to: "/guides",
      tint: { bg: "#DBEAFE", fg: "#1D4ED8" } },
    { key: "contact",     icon: Phone,      label: "Contact Us",
      desc: "Get in touch with our support team",
      to: "/contact",
      tint: { bg: "#D1FAE5", fg: "#047857" } },
    { key: "about",       icon: Info,       label: "About Global Property Mart",
      desc: "About the company & our mission",
      to: "/about",
      tint: { bg: "#E0E7FF", fg: "#4338CA" } },
];

export default function MobileMenuDrawer({ open, onClose }) {
    const navigate = useNavigate();

    // Lock body scroll while drawer is open.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // Close on Escape for accessibility parity with desktop navigation.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const go = (to) => { onClose(); navigate(to); };

    return createPortal(
        <div
            className="lg:hidden fixed inset-0 z-[9999]"
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
            data-testid="mobile-menu-drawer"
        >
            {/* Dim + blur backdrop */}
            <div
                className="absolute inset-0 animate-fade-in"
                style={{ background: "rgba(0, 15, 35, 0.55)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
                onClick={onClose}
                data-testid="mobile-menu-backdrop"
            />

            {/* Drawer panel — slides in from right */}
            <div
                className="absolute inset-y-0 right-0 w-[86%] max-w-sm bg-white shadow-2xl flex flex-col animate-slide-in-right"
                data-testid="mobile-menu-panel"
            >
                {/* ============ HEADER ============ */}
                <div
                    className="text-on-image shrink-0 px-4 pt-4 pb-3 relative overflow-hidden"
                    style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 60%, #003366 100%)" }}
                >
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none opacity-30"
                        style={{ background: "radial-gradient(ellipse 300px 160px at 90% 0%, rgba(218,165,32,0.5), transparent 60%)" }}
                    />
                    <div className="relative flex items-center justify-between">
                        <Link to="/" onClick={onClose} className="flex items-center gap-2.5" data-testid="mobile-menu-brand">
                            <img src="/gpm-logo.png" alt="GPM" className="w-11 h-11 rounded-xl bg-white/10 p-1" />
                            <div className="leading-tight">
                                <div className="font-heading font-extrabold text-white text-[15px] tracking-wide">GLOBAL</div>
                                <div className="font-heading font-extrabold text-[13px] -mt-0.5" style={{ color: "#F5E5A5" }}>PROPERTY MART</div>
                                <div className="text-[9px] uppercase tracking-widest text-white/70 mt-0.5">India&apos;s Trusted Marketplace</div>
                            </div>
                        </Link>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close menu"
                            data-testid="mobile-menu-close"
                            className="w-9 h-9 rounded-full bg-white/12 backdrop-blur-md border border-white/20 flex items-center justify-center active:scale-95 transition-transform"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* ============ INSTALL APP CTA ============ */}
                {/* Prominent banner - hidden automatically when already installed. */}
                <div className="shrink-0 px-3 pt-3" data-testid="mobile-menu-install-slot">
                    <div
                        className="text-on-image rounded-2xl px-3 py-2.5 flex items-center gap-2.5 shadow-md"
                        style={{ background: "linear-gradient(90deg, rgba(218,165,32,0.15), rgba(245,158,11,0.2))", border: "1px solid rgba(218,165,32,0.35)" }}
                    >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)" }}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#001a3d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                                <path d="M12 3v12" /><path d="m8 11 4 4 4-4" /><path d="M3 21h18" />
                            </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading font-extrabold text-[13px] leading-tight" style={{ color: "#002657" }}>Install GPM App</div>
                            <div className="text-[10px] leading-tight" style={{ color: "rgba(0,38,87,0.65)" }}>Faster access · Works offline · FREE</div>
                        </div>
                        <InstallPWAButton
                            variant="compact"
                            className="!px-2.5 !py-1.5 !text-[11px] !bg-white !border-transparent !text-[#001a3d] !font-extrabold"
                        />
                    </div>
                </div>

                {/* ============ MENU LIST ============ */}
                <div className="flex-1 overflow-y-auto overscroll-contain">
                    <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-widest font-extrabold" style={{ color: "#8B6712" }}>
                        Menu Options
                    </div>
                    <ul className="pb-2" data-testid="mobile-menu-list">
                        {MENU_ITEMS.map((it, idx) => {
                            const Icon = it.icon;
                            return (
                                <li key={it.key}>
                                    <button
                                        type="button"
                                        onClick={() => go(it.to)}
                                        data-testid={`mobile-menu-${it.key}`}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-[#f6f7fb] transition-colors"
                                    >
                                        <span
                                            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                                            style={{ background: it.tint.bg }}
                                        >
                                            <Icon className="w-5 h-5" style={{ color: it.tint.fg }} strokeWidth={2.2} />
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-heading font-bold text-[14px] leading-tight" style={{ color: "#002657" }}>
                                                {it.label}
                                            </div>
                                            <div className="text-[11px] mt-0.5 leading-tight line-clamp-1" style={{ color: "rgba(0,38,87,0.6)" }}>
                                                {it.desc}
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "rgba(0,38,87,0.3)" }} />
                                    </button>
                                    {idx < MENU_ITEMS.length - 1 && (
                                        <div className="mx-4 h-px" style={{ background: "rgba(0,38,87,0.06)" }} />
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {/* Footer branding */}
                    <div className="mt-2 mx-3 mb-4 rounded-xl px-3 py-2.5 text-center" style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 100%)" }}>
                        <div className="text-[9px] uppercase tracking-widest font-extrabold" style={{ color: "#F5E5A5" }}>Buy · Sell · Rent · Invest · Grow</div>
                        <div className="text-[10px] text-white/70 mt-0.5">Global Property Mart · v1.0</div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
